import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import {
  generatePath,
  withRouter,
  RouteComponentProps,
} from "react-router-dom";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import InfiniteScroll from "react-infinite-scroll-component";
import AddIcon from "@material-ui/icons/Add";
import { Button } from "@material-ui/core";
import InAppStudioStore from "stores/InAppStudioStore";
import ActiveProgramDictionary from "stores/dictionary/ActiveProgramDictionary";
import Container from "components/Layout/Container";
import Header from "components/Layout/Header";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import Page from "components/Layout/Page";
import ConfirmationDialog from "components/ConfirmationDialog";
import PublishButton from "components/PublishButton";
import RestoreArchivedButton from "components/RestoreArchivedButton";
import Placeholder from "components/Placeholder";
import FilteringControl from "components/FilteringControl";
import { SNACKBAR_TYPES } from "components/Snackbar";
import CategoryCard from "./CategoryCard";
import CategoryForm from "./CategoryForm/CategoryForm";
import ROUTES from "navigation/routes";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";
import { areStatusActiveProgramFiltersEmpty } from "utils/filter";
import { getConfirmArchivationText } from "utils/publish";
import {
  PROGRAM_PERMISSION_TYPES,
  WORKOUT_STRUCTURE_TYPES,
} from "constants/models";
import {
  CategoryCardWrapper,
  ScrollLoader,
  OverflowLoader,
  PageControl,
  FilterContainer,
} from "./styles";
import WorkoutFamilyDictionary from "stores/dictionary/WorkoutFamilyDictionary";

const HEADER_WITH_TITLE_HEIGHT = "270px";

type Props = GlobalPopupProps & SnackbarContextProps & RouteComponentProps & {};

@observer
class InAppStudio extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;

  @computed get categories() {
    return InAppStudioStore.items;
  }

  @computed get categoriesListIsEmpty() {
    return this.categories.length === 0;
  }

  @computed get isFiltersEmpty() {
    const { filters } = InAppStudioStore;

    return areStatusActiveProgramFiltersEmpty(filters);
  }

  @computed get isOverflowLoaderShown() {
    return (
      InAppStudioStore.isCategoryReordering ||
      InAppStudioStore.isCategoryPublishing ||
      InAppStudioStore.isCategoryCopying
    );
  }

  componentDidMount() {
    InAppStudioStore.fetch();
    ActiveProgramDictionary.fetch();
  }

  componentWillUnmount() {
    InAppStudioStore.resetPage();
  }

  fetchMoreCategories = async () => {
    const { setMessage } = this.props;

    try {
      await InAppStudioStore.fetchMoreCategories();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      InAppStudioStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    InAppStudioStore.fetch();
  };

  onFilterClear = () => {
    InAppStudioStore.clearFilters();
    InAppStudioStore.fetch();
  };

  onManageCategory = (initialValues?: any) => {
    const { openPopup } = this.props;

    openPopup({
      key: "ManageCategory",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      confirmation: {
        renderConfirmation: ({ deny, allow }) => (
          <ConfirmationDialog
            title="Discard Changes"
            text="Are you sure you would like to discard changes? All information will be lost."
            declineText="Cancel"
            acceptText="Discard"
            onDecline={deny}
            onAccept={allow}
          />
        ),
        shouldShowConfirmation: this.shouldShowConfirmation,
      },
      render: ({ close }) => (
        <CategoryForm
          isLoading={InAppStudioStore.isCategoryManaging}
          isFormLoading={InAppStudioStore.isCategoryLoading}
          initialValues={initialValues}
          onSubmit={this.onManageCategorySubmit(close)}
          onClose={close}
          onChangeIsDirty={this.onChangeIsPopupDirty}
        />
      ),
    });
  };

  isCollections = (family: any) => {
    if (family?.structureType) {
      return WORKOUT_STRUCTURE_TYPES.COLLECTION === family.structureType;
    } else {
      //  because in the edition, family has not structureType since it is not returned by the GET
      const selectedFamily = WorkoutFamilyDictionary.options.filter(
        e => e?.value?.id === family?.id,
      );
      return (
        selectedFamily.length &&
        selectedFamily[0].value?.structureType ===
          WORKOUT_STRUCTURE_TYPES.COLLECTION
      );
    }
  };

  isWeekly = (family: any) => {
    if (family.structureType) {
      return WORKOUT_STRUCTURE_TYPES.WEEK === family.structureType;
    } else {
      //  because in the edition, family has not structureType since it is not returned by the GET
      const selectedFamily = WorkoutFamilyDictionary.options.filter(
        e => e?.value?.id === family?.id,
      );
      return (
        selectedFamily.length &&
        selectedFamily[0].value.structureType === WORKOUT_STRUCTURE_TYPES.WEEK
      );
    }
  };

  onManageCategorySubmit = (close: () => void) => async (values: any) => {
    const { setMessage } = this.props;
    const {
      file,
      cardFile,
      id,
      activePrograms,
      collection,
      family,
      ...restValues
    } = values;

    const data = {
      title: restValues.title?.trim(),
      subTitle: restValues.subTitle ? restValues.subTitle.trim() : "",
      note: restValues.note ? restValues.note.trim() : "",
      familyIds: [family.id],
      ...(file.dataURL ? { imageUrl: file.dataURL } : { imageUrl: "" }),
      ...(collection &&
        this.isCollections(family) && {
          collectionsId: [parseInt(collection)],
        }),
      ...(activePrograms &&
        activePrograms.length > 0 &&
        !this.isCollections(family) && {
          activePrograms: activePrograms.map(
            ({ id, switchState }: { id: number; switchState: boolean }) => ({
              id: id,
              permission: switchState
                ? PROGRAM_PERMISSION_TYPES.PREVIEW
                : PROGRAM_PERMISSION_TYPES.FULL_ACCESS,
            }),
          ),
        }),
      structureType: this.isWeekly(family) ? 1 : 0,
      startDate:
        this.isWeekly(family) && restValues.startDate
          ? new Date(restValues.startDate).toISOString()
          : undefined,
    };

    try {
      if (!id) {
        await InAppStudioStore.createCategory(data);

        if (cardFile) {
          if (typeof cardFile !== "string") {
            const payloadCard = { cardFile, id };
            await InAppStudioStore.editCategoryCardImage(payloadCard);
          }
        }
      } else {
        await InAppStudioStore.editCategoryInfo(id, data);
        if (cardFile) {
          if (typeof cardFile !== "string") {
            const payloadCard = { cardFile, id };
            await InAppStudioStore.editCategoryCardImage(payloadCard);
          }
        }
      }

      this.resetIsPopupDirty();
      close();
      InAppStudioStore.resetPage();
      InAppStudioStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onAddCategory = () => {
    this.onManageCategory();
  };

  onEditCategory = (category: any) => () => {
    const id = category.id;

    this.onManageCategory({ id });
  };

  openCategoryDetails = (category: any) => () => {
    const path = generatePath(ROUTES.IN_APP_STUDIO_CATEGORY_DETAILS, {
      id: category.id,
    });

    this.props.history.push(path);
  };

  onPublish = (category: any) => async () => {
    const { setMessage } = this.props;
    const { id, publishStatus } = category;

    try {
      await InAppStudioStore.changeCategoryPublishStatus(id, publishStatus);
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onArchive = (category: any) => () => {
    const { openPopup } = this.props;

    const categoryId = category.id;

    const text = getConfirmArchivationText({
      entity: category,
      suffix: "category",
    });

    openPopup({
      key: "ArchiveCategory",
      canCloseByOuterClick: true,
      withoutExitButton: true,
      render: ({ close }) => (
        <ConfirmationDialog
          title={"Archive Category"}
          text={text}
          acceptText={"Archive"}
          declineText={"Cancel"}
          isLoading={InAppStudioStore.isCategoryManaging}
          onDecline={close}
          onAccept={this.archiveCategory(categoryId, close)}
        />
      ),
    });
  };

  archiveCategory = (id: number, close: () => void) => async () => {
    const { setMessage } = this.props;

    try {
      await InAppStudioStore.archiveCategory(id);
      close();
      InAppStudioStore.resetPage();
      InAppStudioStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onCopyCategory = (category: any) => async () => {
    const { setMessage } = this.props;
    const id = category.id;

    try {
      await InAppStudioStore.copyCategory(id);
      InAppStudioStore.resetPage();
      InAppStudioStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onDragEnd = async (result: DropResult) => {
    const { setMessage } = this.props;

    // dropped outside the list
    if (
      !result.destination ||
      !result.source ||
      result.source.index === result.destination.index
    ) {
      return;
    }

    const startIndex = result.source.index;
    const endIndex = result.destination.index;

    try {
      await InAppStudioStore.reorderCategories({ startIndex, endIndex });
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  shouldShowConfirmation = () => {
    return this.isPopupDirty;
  };

  render() {
    return (
      <Container header={<Header controls={<PublishButton />} />}>
        <Page
          title="Categories"
          controls={
            <>
              <PageControl>
                <RestoreArchivedButton />
              </PageControl>
              <PageControl>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={this.onAddCategory}
                >
                  Add category
                </Button>
              </PageControl>
            </>
          }
          isLoading={InAppStudioStore.isLoading}
        >
          <FilterContainer>
            <FilteringControl
              initValue={InAppStudioStore.filters}
              onApply={this.onFilterApply}
              onClear={this.onFilterClear}
              statusFiltering
              accessProgramsFiltering
            />
          </FilterContainer>

          {this.isOverflowLoaderShown && <OverflowLoader />}
          {!this.categoriesListIsEmpty ? (
            <InfiniteScroll
              dataLength={this.categories.length}
              hasMore={InAppStudioStore.hasMoreInfo}
              next={this.fetchMoreCategories}
              height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
              loader={<ScrollLoader />}
            >
              <DragDropContext onDragEnd={this.onDragEnd}>
                <Droppable droppableId="droppable">
                  {(provided, snapshot) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {this.categories.map((category, index) => {
                        return (
                          <Draggable
                            key={category.id}
                            draggableId={category.id.toString()}
                            index={index}
                            isDragDisabled={!this.isFiltersEmpty}
                          >
                            {(provided, snapshot) => (
                              <CategoryCardWrapper
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                              >
                                <CategoryCard
                                  title={category.title}
                                  subtitle={category.subTitle}
                                  note={category.note}
                                  image={category.imageUrl}
                                  publishStatus={category.publishStatus}
                                  isDragging={snapshot.isDragging}
                                  onEdit={this.onEditCategory(category)}
                                  onCopy={
                                    !this.isCollections(category.family)
                                      ? this.onCopyCategory(category)
                                      : undefined
                                  }
                                  onDetailsClick={this.openCategoryDetails(
                                    category,
                                  )}
                                  onArchive={this.onArchive(category)}
                                  onPublish={this.onPublish(category)}
                                  dragHandleProps={provided.dragHandleProps}
                                />
                              </CategoryCardWrapper>
                            )}
                          </Draggable>
                        );
                      })}

                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </InfiniteScroll>
          ) : (
            <Placeholder
              height={280}
              width={280}
              text={"No categories added yet."}
            />
          )}
        </Page>
      </Container>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(InAppStudio)));
